import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { GridsterConfig, GridsterItem, Gridster, GridType } from 'angular-gridster2';
import { TimerService } from '../services/timer/timer.service';
import { ApiService } from '../services/api/api.service';
import { DateService } from '../services/date/date.service';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { CommonModule } from '@angular/common';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { environment } from '../../environments/environment';
import { Subject, takeUntil, map } from 'rxjs';
import { BarChartData, DashboardItemConfig } from '../models/dashboard.model';
import { StatistiekenAtpData, StatistiekenAvpData } from '../models/business-data.model';
import { MarginDetailsModalComponent } from './margin-details-modal/margin-details-modal.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-statistieken',
  templateUrl: './statistieken.component.html',
  styleUrls: ['./statistieken.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    Gridster,
    GridsterItem,
    NgxChartsModule,
    MarginDetailsModalComponent
  ],
})
export class StatistiekenComponent implements OnInit, OnDestroy {
  public currentTime: Date = new Date();
  public options!: GridsterConfig;
  public dashboard!: Array<DashboardItemConfig>;
  public AtpData: StatistiekenAtpData = { Today: '', Week: '', Month: '', Year: '', Buy: '', BuyData: { weeks: [] }, CurrentRemaining: '', Remaining: '', Conversions: null, YearTarget: '' };
  public AvpData: StatistiekenAvpData = { ValuationsMonth: '', ValuationsWeek: '', ValuationsToday: '', BoughtWeek: '', BoughtToday: '', ConversionWeek: '' }
  public daysOfWeek = ['Za', 'Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr'];

  public single: BarChartData[] = [];
  public AvpBarChartData: BarChartData[] = [];

  private destroy$ = new Subject<void>();
  private reloadInterval: any;
  private scheduledTimeouts: any[] = [];

  public showXAxis: boolean = true;
  public showYAxis: boolean = true;
  public gradient: boolean = true;
  public showLegend: boolean = false;
  public showXAxisLabel: boolean = true;
  public xAxisLabel = 'Dag';
  public showYAxisLabel: boolean = true;
  public yAxisLabel = 'Aantal taxaties';
  public animations: boolean = true;
  public showDataLabel: boolean = true;

  public colorScheme: any = {
    domain: ['#40b7a2', '#1b5674', '#e7518b']
  };

  public avpColorScheme: any = {
    domain: ['#40b7a2', '#1b5674']
  };

  public version: string = 'n/a';

  @ViewChild('marginModal') marginModal!: MarginDetailsModalComponent;

  constructor(
    public scheduler: TimerService,
    public apiService: ApiService,
    public dateService: DateService,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private router: Router) { }

  public ngOnInit() {
    if (environment.production) {
      this.apiService.getAppVersionFromNgSw().then(v => this.version = v || 'n/a');
    }

    this.initializeClock();
    this.setupGridster();
    this.setupDashboard();
    this.initDataFetchers();
    this.reloadInterval = setInterval(() => window.location.reload(), 43200000);
  }

  public ngOnDestroy() {
    if (this.reloadInterval) {
      clearInterval(this.reloadInterval);
    }
    this.scheduledTimeouts.forEach(timeout => clearTimeout(timeout));
    this.scheduledTimeouts = [];

    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeClock() {
    this.dateService.currentTime$.pipe(takeUntil(this.destroy$)).subscribe(time => {
      this.currentTime = time;
      this.cdr.markForCheck();
    });
  }

  private setupGridster() {
    this.options = {
      gridType: GridType.Fit,
      displayGrid: 'onDrag&Resize',
      minCols: 24,
      minRows: 12,
      maxCols: 48,
      margin: 10,
      outerMargin: true,
      draggable: {
        enabled: true,
        ignoreContentClass: 'gridster-item-content',
        dragHandleClass: 'drag-handler',
      },
      resizable: {
        enabled: true,
      }
    };
  }

  private setupDashboard() {
    this.dashboard = [
      { cols: 24, rows: 8, y: 0, x: 0, hasContent: true, contentType: 'TaxAmount' },
      { cols: 9, rows: 6, y: 8, x: 0, hasContent: true, contentType: 'CarAmount' },
      { cols: 9, rows: 6, y: 14, x: 0, hasContent: true, contentType: 'AVP' },
      { cols: 9, rows: 7, y: 8, x: 9, hasContent: true, contentType: 'Conversions' },
      { cols: 9, rows: 5, y: 15, x: 9, hasContent: true, contentType: 'AVP-details' },
      { cols: 6, rows: 12, y: 8, x: 18, hasContent: true, contentType: 'CarCounter' },
    ];
  }

  private initDataFetchers() {
    this.generateTable();
    this.generateAvpChart();
    this.scheduleUpdate(() => this.getTaxThisWeek(), 60000, 600000);
    this.scheduleUpdate(() => this.getTaxThisMonth(), 60000, 600000);
    this.scheduleUpdate(() => this.getTaxToday(), 60000, 600000);
    this.scheduleUpdate(() => this.getForSale(), 60000, 600000);
    this.scheduleUpdate(() => this.getBuyAmounts(), 60000, 600000);
    this.scheduleUpdate(() => this.getCurrentRemaining(), 120000, 600000);
    this.scheduleUpdate(() => this.getRemaining(), 120000, 600000);
    this.scheduleUpdate(() => this.getAvSaleMargin(), 100000, 600000);
    this.scheduleUpdate(() => this.getYearTarget(), 60000, 600000);
    this.scheduleUpdate(() => this.getAvpStatistics(), 60000, 600000);
    this.scheduleUpdate(() => this.generateTable(), 120000, 300000); // Update chart every 5 minutes
  }

  private scheduleUpdate(callback: () => void, successDelay: number, errorDelay: number) {
    callback();
    const retry = () => {
      if (this.destroy$.closed) return;
      this.scheduleUpdate(callback, successDelay, errorDelay);
    };
    const timeoutId = setTimeout(retry, successDelay);
    this.scheduledTimeouts.push(timeoutId);
  }

  private generateTable(day = 31) {
    const currentDate = new Date();
    this.single = []; // Reset the chart data

    const startDate = new Date(currentDate);
    startDate.setDate(currentDate.getDate() - (day - 1));
    const from = this.dateService.startOfDay(startDate);
    const to = this.dateService.endOfDay(currentDate);

    // Single Elasticsearch query with date histogram aggregation
    const query = JSON.stringify({
      size: 0,
      query: {
        bool: {
          must: [
            { range: { "Properties.created": { gte: from, lte: to } } }
          ]
        }
      },
      aggs: {
        by_day: {
          date_histogram: {
            field: "Properties.created",
            calendar_interval: "day",
            format: "dd-MM",
            time_zone: "Europe/Amsterdam"
          },
          aggs: {
            by_origin_and_type: {
              filters: {
                filters: {
                  regular_b2b: {
                    bool: {
                      must: [
                        { term: { "Properties.originChannelType": "b2b" } },
                        { term: { "Properties.valuationType": "regular" } },

                      ]
                    }
                  },
                  urgent_b2b: {
                    bool: {
                      must: [
                        { term: { "Properties.originChannelType": "b2b" } },
                        { term: { "Properties.valuationType": "urgent" } },
                      ]
                    }
                  },
                  avp_c2b: {
                    bool: {
                      must: [
                        { term: { "Properties.originChannelType": "c2b" } },
                      ]
                    }
                  }
                }
              }
            }
          }
        }
      }
    });

    this.apiService.statisticsSearch(environment.elasticIndexName, query).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        const dayBuckets = result.data.aggregations?.by_day?.buckets || [];

        // Process the aggregation results
        dayBuckets.forEach((bucket: any) => {
          const label = bucket.key_as_string;
          const filters = bucket.by_origin_and_type.buckets;

          this.single.push({
            name: label,
            series: [
              { name: `Spoed: ${label}`, value: filters.urgent_b2b?.doc_count || 0 },
              { name: `24 uur: ${label}`, value: filters.regular_b2b?.doc_count || 0 },
              { name: `AVP: ${label}`, value: filters.avp_c2b?.doc_count || 0 }
            ]
          });
        });

        this.single = [...this.single]; // Force update
      }
    });
  }

  generateAvpChart(day: number = 14) {
    const currentDate = new Date();
    this.AvpBarChartData = []; // Clear previous data

    const fetchDay = (remainingDays: number) => {
      if (remainingDays <= 0) {
        // All data fetched, now reverse to show oldest to newest
        this.AvpBarChartData.reverse();
        this.AvpBarChartData = [...this.AvpBarChartData];
        return;
      }

      const calDate = new Date(currentDate);
      calDate.setDate(currentDate.getDate() - (day - remainingDays));

      const dateStr = (d: Date) => `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`;
      const from = `${dateStr(calDate)} 00:00:00`;
      const to = `${dateStr(calDate)} 23:59:59`;
      const label = `${calDate.getDate()}-${calDate.getMonth() + 1}`;

      const boughtQuery = JSON.stringify({
        AND: [["StatusAudits.created", "gte", from], ["StatusAudits.created", "lte", to], ["OriginChannelType.name", "eq", "c2b"], ["ResultType.name", "eq", "bought"], ["StatusAuditStatusType.name", "eq", "buy"]
        ]
      });

      const totalQuery = JSON.stringify({
        AND: [
          ["StatusAudits.created", "gte", from], ["StatusAudits.created", "lte", to], ["OriginChannelType.name", "eq", "c2b"], ["ResultType.name", "neq", "bought"]
        ]
      });

      this.apiService.getValuations(1, 1, '', boughtQuery, false, null).subscribe(resultBought => {
        this.apiService.getValuations(1, 1, '', totalQuery, false, null).subscribe(resultTotal => {
          this.AvpBarChartData.push({
            name: label,
            series: [
              {
                name: `Taxaties: ${label}`,
                value: parseInt(resultTotal.paginator.records)
              },
              {
                name: `Gekocht: ${label}`,
                value: parseInt(resultBought.paginator.records)
              }
            ]
          });
          this.AvpBarChartData = [...this.AvpBarChartData]; // trigger change detection
          setTimeout(() => fetchDay(remainingDays - 1), 300);
        });
      });
    };

    fetchDay(day);
  }

  private getAvpStatistics() {
    const date = this.dateService.subtractDate(31);
    const query = JSON.stringify({
      size: 0,
      query: {
        bool: {
          must: [
            { range: { "Properties.created": { gte: `${date.CalDate} 00:00:00`, lte: `${date.TodyDate} 23:59:59` } } },
            { term: { "Properties.originChannelType": "c2b" } }
          ]
        }
      },
      aggs: {
        by_day: {
          date_histogram: {
            field: "Properties.created",
            calendar_interval: "day",
            format: "yyyy-MM-dd",
            time_zone: "Europe/Amsterdam"
          },
          aggs: {
            bought: {
              filter: {
                term: { "Properties.resultType": "bought" }
              }
            }
          }
        }
      }
    });

    this.apiService.statisticsSearch(environment.elasticIndexName, query).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        const buckets = result.data.aggregations?.by_day?.buckets || [];

        // Calculate totals
        let monthTotal = 0;
        let weekTotal = 0;
        let todayTotal = 0;
        let weekBought = 0;
        let todayBought = 0;

        const todayStr = this.dateService.formatDate(new Date());
        const sixDaysAgo = new Date();
        sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);

        buckets.forEach((bucket: any) => {
          const bucketDate = bucket.key_as_string;
          const totalCount = bucket.doc_count || 0;
          const boughtCount = bucket.bought?.doc_count || 0;

          // Month total (all 31 days)
          monthTotal += totalCount;

          // Week total (last 6 days + today = 7 days)
          const bucketDateObj = new Date(bucketDate);
          if (bucketDateObj >= sixDaysAgo) {
            weekTotal += totalCount;
            weekBought += boughtCount;
          }

          // Today
          if (bucketDate === todayStr) {
            todayTotal = totalCount;
            todayBought = boughtCount;
          }
        });

        // Update all AVP data
        this.AvpData.ValuationsMonth = String(monthTotal);
        this.AvpData.ValuationsWeek = String(weekTotal);
        this.AvpData.ValuationsToday = String(todayTotal);
        this.AvpData.BoughtToday = String(todayBought);
        this.AvpData.BoughtWeek = String(weekBought);
        this.AvpData.ConversionWeek = weekTotal > 0
          ? ((weekBought / weekTotal) * 100).toFixed(2)
          : '0.00';
      }
    });
  }

  private getTaxToday() {
    const date = this.dateService.subtractDate(0);
    const fromDate = this.dateService.startOfDay(new Date());
    const toDate = this.dateService.endOfDay(new Date());

    const oldValue = this.AtpData.Today;
    this.apiService.statisticsCount(environment.elasticIndexName, this.buildCountQuery([
      { field: 'Properties.created', operator: 'gte', value: fromDate },
      { field: 'Properties.created', operator: 'lte', value: toDate },
    ])).pipe(
      map(result => result.data.count || 0),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (current) => {
        if (oldValue !== String(current)) {
          const diff = current - Number(this.AtpData.Today);
          this.AtpData.Week = String(Number(this.AtpData.Week) + diff);
          this.AtpData.Month = String(Number(this.AtpData.Month) + diff);
          this.AtpData.Year = String(Number(this.AtpData.Year) + diff);
        }
        this.AtpData.Today = String(current);
      }
    });
  }

  private getTaxThisWeek() {
    const date = this.dateService.subtractDate(6);
    this.apiService.statisticsCount(environment.elasticIndexName, this.buildCountQuery([
      { field: 'Properties.created', operator: 'gte', value: `${date.CalDate} 00:00:00` },
      { field: 'Properties.created', operator: 'lte', value: `${date.TodyDate} 23:59:59` }
    ])).pipe(
      map(result => result.data.count || 0),
      takeUntil(this.destroy$)
    ).subscribe({
      next: count => {
        this.AtpData.Week = String(count);
      }
    });
  }

  private getTaxThisMonth() {
    const date = this.dateService.subtractDate(31);
    this.apiService.statisticsCount(environment.elasticIndexName, this.buildCountQuery([
      { field: 'Properties.created', operator: 'gte', value: `${date.CalDate} 00:00:00` },
      { field: 'Properties.created', operator: 'lte', value: `${date.TodyDate} 23:59:59` }
    ])).pipe(
      map(result => result.data.count || 0),
      takeUntil(this.destroy$)
    ).subscribe({
      next: count => {
        this.AtpData.Month = String(count);
      }
    });
  }

  private getForSale() {
    this.apiService.statisticsCount(environment.elasticIndexName, this.buildCountQuery([
      { field: 'Properties.statusType', operator: 'eq', value: 'bought' },
      { field: 'AssignedCompany.id', operator: 'eq', value: 1095 }
    ])).pipe(
      map(result => result.data.count || 0),
      takeUntil(this.destroy$)
    ).subscribe({
      next: count => {
        this.AtpData.Buy = String(count);
      }
    });
  }

  private getBuyAmounts() {
    this.apiService.getBuyAmounts().pipe(takeUntil(this.destroy$)).subscribe({
      next: result => {
        this.AtpData.BuyData = result;
      }
    });
  }

  private getYearTarget() {
    this.apiService.getYearTargets().pipe(takeUntil(this.destroy$)).subscribe({
      next: result => {
        const targetGoal = result.year_target;
        const currentGoal = result.months.reduce((sum: number, target: any) => sum + target.value, 0);
        this.AtpData.YearTarget = (currentGoal > targetGoal ? '+' : '-') + Math.abs(currentGoal - targetGoal);
      }
    });
  }

  private getCurrentRemaining() {
    const now = new Date();
    this.apiService.statisticsCount(environment.elasticIndexName, this.buildCountQuery([
      { field: 'Properties.statusType', operator: 'eq', value: 'new' },
      { field: 'BidList.BidTerm.closeDateBid', operator: 'gte', value: this.dateService.formatDateTime(now) }
    ])).pipe(
      map(result => result.data.count || 0),
      takeUntil(this.destroy$)
    ).subscribe({
      next: count => {
        this.AtpData.CurrentRemaining = String(count);
      }
    });
  }

  private getRemaining() {
    const now = new Date();
    const date = this.dateService.subtractDate(30);

    this.apiService.statisticsCount(environment.elasticIndexName, this.buildCountQuery([
      { field: 'Properties.statusType', operator: 'eq', value: 'new' },
      { field: 'BidList.BidTerm.closeDateBid', operator: 'gte', value: `${date.CalDate} 00:00:00` },
      { field: 'BidList.BidTerm.closeDateBid', operator: 'lte', value: this.dateService.formatDateTime(now) }
    ])).pipe(
      map(result => result.data.count || 0),
      takeUntil(this.destroy$)
    ).subscribe({
      next: count => {
        this.AtpData.Remaining = String(count);
      }
    });
  }

  private getAvSaleMargin() {
    // Build the week array starting from Saturday (like PHP did)
    const currentDate = new Date();

    // Find the most recent Saturday (or today if today is Saturday)
    const saturday = new Date(currentDate);
    const dayOfWeek = saturday.getDay(); // 0 = Sunday, 6 = Saturday
    const daysToSaturday = (dayOfWeek + 1) % 7; // Days to go back to Saturday
    saturday.setDate(currentDate.getDate() - daysToSaturday);

    // Build wildcard clauses for each day in the week
    const wildcardClauses = [];
    for (let i = 0; i <= 6; i++) {
      const date = new Date(saturday);
      date.setDate(saturday.getDate() + i);
      const dateKey = this.dateService.formatDate(date);
      wildcardClauses.push({
        wildcard: { "Properties.marginDate.keyword": `${dateKey}*` }
      });
    }

    const query = JSON.stringify({
      size: 0,
      query: {
        bool: {
          must: [
            { bool: { should: wildcardClauses, minimum_should_match: 1 } },
            { exists: { field: "Properties.marginActual" } }
          ]
        }
      },
      aggs: {
        by_day: {
          terms: {
            script: {
              source: "doc['Properties.marginDate.keyword'].value.substring(0, 10)",
              lang: "painless"
            },
            size: 10
          },
          aggs: {
            total_valuations: { value_count: { field: "Properties.id" } },
            total_sale: { sum: { field: "Properties.assignedPrice" } },
            total_purchase: { sum: { field: "Properties.dgwAmount" } },
            total_margin: { sum: { field: "Properties.marginActual" } },
            avg_margin: { avg: { field: "Properties.marginActual" } }
          }
        }
      }
    });

    this.apiService.statisticsSearch(environment.elasticIndexName, query).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        const buckets = result.data.aggregations?.by_day?.buckets || [];

        // Create a map of date -> stats
        const dataMap = new Map<string, any>();
        buckets.forEach((bucket: any) => {
          const dateKey = bucket.key_as_string || bucket.key;
          dataMap.set(dateKey, {
            total_valuations: bucket.total_valuations?.value || 0,
            total_sale: Math.round(bucket.total_sale?.value || 0),
            total_purchase: Math.round(bucket.total_purchase?.value || 0),
            total_margin: Math.round((bucket.total_margin?.value || 0) * 100) / 100,
            avg_margin: Math.round((bucket.avg_margin?.value || 0) * 100) / 100
          });
        });

        // Build the 7-day array starting from Saturday (Saturday + 0 to Saturday + 6)
        const stats: any[] = [];
        for (let i = 0; i <= 6; i++) {
          const date = new Date(saturday);
          date.setDate(saturday.getDate() + i);
          const dateKey = this.dateService.formatDate(date);

          const dayData = dataMap.get(dateKey);

          stats.push({
            date: dateKey,
            total_valuations: dayData?.total_valuations || 0,
            total_sale: dayData?.total_sale || 0,
            total_purchase: dayData?.total_purchase || 0,
            total_margin: dayData?.total_margin || 0,
            avg_margin: dayData?.avg_margin || 0
          });
        }

        this.AtpData.Conversions = stats;
      }
    });
  }

  public get avpCards() {
    return [
      { key: 'ValuationsToday', label: 'Taxaties Vandaag', value: this.AvpData.ValuationsToday },
      { key: 'ValuationsWeek', label: 'Afgelopen 7 dagen', value: this.AvpData.ValuationsWeek },
      { key: 'ValuationsMonth', label: 'Afgelopen 31 dagen', value: this.AvpData.ValuationsMonth },
      { key: 'BoughtToday', label: 'Gekocht vandaag', value: this.AvpData.BoughtToday },
      { key: 'BoughtWeek', label: 'Gekocht 7 dagen', value: this.AvpData.BoughtWeek },
      { key: 'ConversionWeek', label: 'Conversie 7 dagen', value: this.AvpData.ConversionWeek },
    ];
  }

  /**
   * Open the margin details modal for a specific date
   */
  public openMarginModal(date: string): void {
    if (this.marginModal) {
      this.marginModal.open(date);
    }
  }

  /**
   * Build an ElasticSearch count query for valuations
   */
  private buildCountQuery(filters: Array<{
    field: string;
    operator: 'gte' | 'lte' | 'eq' | 'in';
    value: any;
  }>): string {
    const mustClauses = filters.map(f => {
      switch (f.operator) {
        case 'gte':
        case 'lte':
          return { range: { [f.field]: { [f.operator]: f.value } } };
        case 'eq':
          return { term: { [f.field]: f.value } };
        case 'in':
          return { terms: { [f.field]: f.value } };
        default:
          throw new Error(`Unknown operator: ${f.operator}`);
      }
    });

    return JSON.stringify({
      query: {
        bool: {
          must: mustClauses
        }
      }
    });
  }

  public logout(): void {
    this.authenticationService.logout();
  }
}
